import { useMemo, useRef, useState } from 'react'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import GroupsTable from '../components/GroupsTable'

import { useDeleteAccountGroupMutation, useDeleteAccountGroupsMutation, useGetAccountGroupsQuery } from '@/core/api'

import DeleteConfirmationDialog from '@/components/ui/dialogs/DeleteConfirmationDialog'

import { getApiErrorMessage } from '@/utils/errors'

export default function GroupsTableContainer() {
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const { data: accountGroups, isLoading } = useGetAccountGroupsQuery({}, { refetchOnMountOrArgChange: true })
  const [deleteGroup, { isLoading: isDeletingGroup }] = useDeleteAccountGroupMutation()
  const [deleteGroups, { isLoading: isDeletingGroups }] = useDeleteAccountGroupsMutation()

  const accountGroupsData = useMemo(
    () =>
      accountGroups?.map((accountGroup) => ({
        id: accountGroup._id,
        name: accountGroup.name,
        totalAccounts: accountGroup.totalAccounts ?? 0,
        totalPolicies: accountGroup.policies.length,
      })) ?? [],
    [accountGroups]
  )

  const deletingGroupId = useRef<string>()
  const deletingGroupIds = useRef<string[]>()
  const [openDeleteConfirmationDialog, setOpenDeleteConfirmationDialog] = useState(false)

  return (
    <>
      <GroupsTable
        data={accountGroupsData}
        loading={isLoading}
        onDeleteClick={(groupId) => {
          deletingGroupId.current = groupId
          setOpenDeleteConfirmationDialog(true)
        }}
        onDeleteManyClick={(groupIds) => {
          deletingGroupIds.current = groupIds
          setOpenDeleteConfirmationDialog(true)
        }}
      />
      <DeleteConfirmationDialog
        isDeleting={isDeletingGroup || isDeletingGroups}
        maxWidth={800}
        onClose={() => {
          setOpenDeleteConfirmationDialog(false)
        }}
        onDelete={async () => {
          try {
            if (deletingGroupId.current) {
              await deleteGroup(deletingGroupId.current).unwrap()
              deletingGroupId.current = undefined
            } else if (deletingGroupIds.current) {
              await deleteGroups(deletingGroupIds.current).unwrap()
              deletingGroupIds.current = undefined
            }
          } catch (error) {
            enqueueSnackbar(getApiErrorMessage(error, t('notification.accountGroup.delete.error')), { variant: 'error' })
          }
        }}
        open={openDeleteConfirmationDialog}
        title={t('dialog.deleteConfirmation.accountGroup.title')}
        message={t('dialog.deleteConfirmation.accountGroup.message')}
      />
    </>
  )
}
